// Searchable combobox for picking an existing material. Replaces the
// plain `<select>` that became unusable once an org accumulates more
// than ~20 materials. Lightweight: no external deps, no popper, no
// virtual list — a single input plus an absolutely-positioned dropdown
// of up to `LIMIT` matches scored by substring overlap.
//
// Used by:
//   - `ManufacturingModelsPendingLinesEditor` (post-save reconcile UI)
//   - `ManufacturingDictatedRecipeDraftEditor` (inline match inside the
//      dictate review surface)
//
// BOM line editing uses `ManufacturingComboboxPicker` instead — it shows
// the current selection on the closed trigger and opens a searchable list.
//
// The component is intentionally uncontrolled w.r.t. its own search
// text; callers render any "currently selected" chrome themselves so
// the same widget works both as a one-shot picker (reconcile) and a
// re-pick affordance (Change).

import { useEffect, useMemo, useRef, useState } from "react";
import { matchesLooseCatalogQuery } from "@/lib/manufacturing/looseCatalogQuery";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import { mfgMdInputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

const LIMIT = 50;

function normalize(s: string): string {
  return s.trim().toLowerCase();
}

function score(material: MfgMaterial, q: string): number {
  if (!q) return 1;
  const code = normalize(material.code);
  const name = normalize(material.name);
  if (code === q || name === q) return 1000;
  if (code.startsWith(q)) return 500;
  if (name.startsWith(q)) return 400;
  if (code.includes(q)) return 200;
  if (name.includes(q)) return 100;
  if (matchesLooseCatalogQuery(`${material.code} ${material.name}`, q)) return 50;
  return 0;
}

function filterMaterials(
  materials: ReadonlyArray<MfgMaterial>,
  query: string,
): MfgMaterial[] {
  const q = normalize(query);
  if (!q) return materials.slice(0, LIMIT);
  return materials
    .map((m) => ({ m, s: score(m, q) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s || a.m.name.localeCompare(b.m.name))
    .slice(0, LIMIT)
    .map((x) => x.m);
}

export type MaterialSearchableSelectProps = {
  materials: ReadonlyArray<MfgMaterial>;
  onSelect: (material: MfgMaterial) => void;
  /** Pre-fill the search text. Useful when seeding the picker with the
   *  heard / typed material name from a pending line so the operator
   *  sees the closest matches immediately. */
  initialQuery?: string;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Optional id of the currently selected material — excluded from
   *  the suggestion list so "Change" doesn't suggest the value it's
   *  trying to replace. */
  excludeId?: string | null;
};

export function MaterialSearchableSelect({
  materials,
  onSelect,
  initialQuery,
  placeholder = "Search materials by code or name…",
  disabled,
  autoFocus,
  excludeId,
}: MaterialSearchableSelectProps) {
  const activeMaterials = useMemo(
    () =>
      materials.filter(
        (m) => m.is_active && (!excludeId || m.id !== excludeId),
      ),
    [materials, excludeId],
  );

  const [query, setQuery] = useState<string>(initialQuery ?? "");
  const [open, setOpen] = useState<boolean>(false);
  const [activeIdx, setActiveIdx] = useState<number>(0);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const filtered = useMemo(
    () => filterMaterials(activeMaterials, query),
    [activeMaterials, query],
  );

  useEffect(() => {
    setActiveIdx(0);
  }, [query]);

  const commit = (m: MfgMaterial) => {
    onSelect(m);
    setQuery("");
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        className={mfgMdInputClass}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIdx((i) => Math.max(i - 1, 0));
          } else if (e.key === "Enter") {
            const pick = filtered[activeIdx];
            if (pick) {
              e.preventDefault();
              commit(pick);
            }
          } else if (e.key === "Escape") {
            e.preventDefault();
            setOpen(false);
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-autocomplete="list"
        aria-expanded={open}
        role="combobox"
      />
      {open && filtered.length > 0 ? (
        <ul
          role="listbox"
          className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
        >
          {filtered.map((m, idx) => {
            const isActive = idx === activeIdx;
            return (
              <li key={m.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  title={`${m.code} — ${m.name}`}
                  className={
                    "block w-full truncate px-3 py-1.5 text-left text-sm transition " +
                    (isActive
                      ? "bg-indigo-50 dark:bg-zinc-800"
                      : "hover:bg-indigo-50 dark:hover:bg-zinc-800")
                  }
                  onMouseEnter={() => setActiveIdx(idx)}
                  onClick={() => commit(m)}
                >
                  <span className="font-medium text-gray-900 dark:text-zinc-100">
                    {m.code}
                  </span>
                  <span className="text-gray-500 dark:text-zinc-400"> — {m.name}</span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
      {open && filtered.length === 0 ? (
        <div className="absolute z-30 mt-1 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-500 shadow-lg dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400">
          No matching materials.
        </div>
      ) : null}
    </div>
  );
}
